import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Target,
  TrendingUp,
  Calendar,
  Phone,
  Home,
  DollarSign,
  Save,
  Edit3,
  Copy,
  BarChart3,
  X,
  MessageSquare,
  Mail,
  FileText,
  Share2
} from 'lucide-react';
import {
  getMonthlyGoals,
  createMonthlyGoal,
  updateMonthlyGoal
} from '../../../api';
import toast from 'react-hot-toast';
import PageHeader from '../../../components/shared/PageHeader';
import { apiCache, cacheKeys, cachedApiCall, invalidateCache } from '../../../lib/cache';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { hasAdminAccess } from "../../../utils/roleAccess";

const KPIPage = () => {
  const { user } = useSelector((state) => state.auth);
  const isAdmin = hasAdminAccess(user);

  const [currentGoal, setCurrentGoal] = useState(null);
  const [allGoalsData, setAllGoalsData] = useState([]);
  const [organizeBy, setOrganizeBy] = useState('D');

  // Metrics whose "current progress" is auto-computed by the backend from real
  // CRM activity (calls, appraisals, listings, sales). These are read-only in the
  // Edit modal; the rest stay manual. Maps the backend current_* field -> FE id.
  const AUTO_FIELD_TO_ID = {
    current_calls: 'calls_made',
    current_appraisal: 'appraisals_completed',
    current_listing: 'listings_won',
    current_sales: 'sales_closed'
  };
  const DEFAULT_AUTO_IDS = Object.values(AUTO_FIELD_TO_ID);
  const [autoTrackedIds, setAutoTrackedIds] = useState(DEFAULT_AUTO_IDS);

  // KPI Items according to the image
  const kpiItems = [
    { id: 'calls_made', label: 'Call Connects', icon: Phone, color: '#55C5D0', unit: 'calls' },
    { id: 'text_sent', label: 'Text Sent', icon: MessageSquare, color: '#55C5D0', unit: 'texts' },
    { id: 'email_sent', label: 'Email Sent', icon: Mail, color: '#55C5D0', unit: 'emails' },
    { id: 'door_knocks', label: 'Door Knocks', icon: Home, color: '#55C5D0', unit: 'knocks' },
    { id: 'letter_drops', label: 'Letter Drops', icon: FileText, color: '#55C5D0', unit: 'letters' },
    { id: 'social_media', label: 'Social Media', icon: Share2, color: '#55C5D0', unit: 'posts' },
    { id: 'appraisals_completed', label: 'Appraisals Completed', icon: Target, color: '#55C5D0', unit: 'appraisals' },
    { id: 'listings_won', label: 'Listings Won', icon: TrendingUp, color: '#55C5D0', unit: 'listings' },
    { id: 'sales_closed', label: 'Sales Closed', icon: DollarSign, color: '#55C5D0', unit: 'sales' }
  ];

  const [goals, setGoals] = useState({
    calls_made: 0,
    text_sent: 0,
    email_sent: 0,
    door_knocks: 0,
    letter_drops: 0,
    social_media: 0,
    appraisals_completed: 0,
    listings_won: 0,
    sales_closed: 0
  });

  const [targets, setTargets] = useState({
    calls_made: 50,
    text_sent: 50,
    email_sent: 50,
    door_knocks: 50,
    letter_drops: 50,
    social_media: 50,
    appraisals_completed: 50,
    listings_won: 50,
    sales_closed: 50
  });

  // Annual GCI (commission) target in dollars. Stored on MonthlyGoal.commission_target,
  // also drives the "GCI Target Progress" widget on the main dashboard. Defaults to $1M.
  const [commissionTarget, setCommissionTarget] = useState(1000000);

  const [editingGoals, setEditingGoals] = useState(false);
  const [loading, setLoading] = useState(true);

  const [progressValues, setProgressValues] = useState({
    calls_made: 0,
    text_sent: 0,
    email_sent: 0,
    door_knocks: 0,
    letter_drops: 0,
    social_media: 0,
    appraisals_completed: 0,
    listings_won: 0,
    sales_closed: 0
  });

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    loadGoals();
  }, []);

  const loadGoals = async (forceRefresh = false) => {
    try {
      if (!apiCache.has(cacheKeys.kpi(`${currentMonth}-${currentYear}`)) || forceRefresh) {
        setLoading(true);
      }

      const res = await cachedApiCall(
        cacheKeys.kpi(`${currentMonth}-${currentYear}`),
        () => getMonthlyGoals(),
        10 * 60 * 1000,
        { forceRefresh }
      );

      const allGoals = Array.isArray(res.data)
        ? res.data
        : (res.data?.results || []);

      setAllGoalsData(allGoals);

      const monthGoal = allGoals.find(
        g => g.month === currentMonth && g.year === currentYear
      );

      if (monthGoal) {
        setCurrentGoal(monthGoal);
        setCommissionTarget(monthGoal.commission_target || 1000000);
        if (Array.isArray(monthGoal.auto_tracked_fields)) {
          setAutoTrackedIds(
            monthGoal.auto_tracked_fields.map(f => AUTO_FIELD_TO_ID[f]).filter(Boolean)
          );
        }
        setTargets({
          calls_made: monthGoal.calls_target || 50,
          text_sent: monthGoal.text_target || 50,
          email_sent: monthGoal.email_target || 50,
          door_knocks: monthGoal.door_knocks_target || 50,
          letter_drops: monthGoal.letter_drops_target || 50,
          social_media: monthGoal.social_media_target || 50,
          appraisals_completed: monthGoal.appraisal_target || 50,
          listings_won: monthGoal.listing_target || 50,
          sales_closed: monthGoal.sales_target || 50
        });
        setGoals({
          calls_made: monthGoal.current_calls || 0,
          text_sent: monthGoal.current_text || 0,
          email_sent: monthGoal.current_email || 0,
          door_knocks: monthGoal.current_door_knocks || 0,
          letter_drops: monthGoal.current_letter_drops || 0,
          social_media: monthGoal.current_social_media || 0,
          appraisals_completed: monthGoal.current_appraisal || 0,
          listings_won: monthGoal.current_listing || 0,
          sales_closed: monthGoal.current_sales || 0
        });
        setProgressValues({
          calls_made: monthGoal.current_calls || 0,
          text_sent: monthGoal.current_text || 0,
          email_sent: monthGoal.current_email || 0,
          door_knocks: monthGoal.current_door_knocks || 0,
          letter_drops: monthGoal.current_letter_drops || 0,
          social_media: monthGoal.current_social_media || 0,
          appraisals_completed: monthGoal.current_appraisal || 0,
          listings_won: monthGoal.current_listing || 0,
          sales_closed: monthGoal.current_sales || 0
        });
      }
    } catch (error) {
      console.error('Error loading goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGoals = async () => {
    try {
      setLoading(true);
      const targetData = {
        calls_target: parseInt(targets.calls_made) || 0,
        text_target: parseInt(targets.text_sent) || 0,
        email_target: parseInt(targets.email_sent) || 0,
        door_knocks_target: parseInt(targets.door_knocks) || 0,
        letter_drops_target: parseInt(targets.letter_drops) || 0,
        social_media_target: parseInt(targets.social_media) || 0,
        appraisal_target: parseInt(targets.appraisals_completed) || 0,
        listing_target: parseInt(targets.listings_won) || 0,
        sales_target: parseInt(targets.sales_closed) || 0,
        commission_target: parseInt(commissionTarget) || 0,

        // Progress values
        current_calls: parseInt(progressValues.calls_made) || 0,
        current_text: parseInt(progressValues.text_sent) || 0,
        current_email: parseInt(progressValues.email_sent) || 0,
        current_door_knocks: parseInt(progressValues.door_knocks) || 0,
        current_letter_drops: parseInt(progressValues.letter_drops) || 0,
        current_social_media: parseInt(progressValues.social_media) || 0,
        current_appraisal: parseInt(progressValues.appraisals_completed) || 0,
        current_listing: parseInt(progressValues.listings_won) || 0,
        current_sales: parseInt(progressValues.sales_closed) || 0
      };

      if (currentGoal?.id) {
        await updateMonthlyGoal(currentGoal.id, targetData);
        toast.success('KPIs updated successfully!');
      } else {
        const createData = {
          month: currentMonth,
          year: currentYear,
          ...targetData,
          current_commission: 0
        };
        await createMonthlyGoal(createData);
        toast.success('KPIs created successfully!');
      }

      invalidateCache.kpi();
      await loadGoals(true);
      setEditingGoals(false);
    } catch (error) {
      toast.error('Failed to save goals');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get scaling factor and formatted values
  // Using 5-day work week calculation:
  // - 1 month = 4 weeks (simplified for clean numbers)
  // - 1 week = 5 work days
  // - 1 month = 20 work days (4 weeks × 5 days)
  const getScaledValues = (current, target, type) => {
    const WEEKS_PER_MONTH = 4;
    const WORK_DAYS_PER_WEEK = 5;
    const WORK_DAYS_PER_MONTH = WEEKS_PER_MONTH * WORK_DAYS_PER_WEEK; // 20
    const MONTHS_PER_YEAR = 12;

    let scaledTarget = target;
    let scaledCurrent = current;

    if (type === 'D') {
      // Daily: divide monthly target by work days per month
      // Example: 200 calls/month ÷ 20 work days = 10 calls/day
      scaledTarget = target / WORK_DAYS_PER_MONTH;
      scaledCurrent = current / WORK_DAYS_PER_MONTH;
    } else if (type === 'W') {
      // Weekly: divide monthly target by weeks per month
      // Example: 200 calls/month ÷ 4 weeks = 50 calls/week
      scaledTarget = target / WEEKS_PER_MONTH;
      scaledCurrent = current / WEEKS_PER_MONTH;
    } else if (type === 'Y') {
      // Yearly: multiply monthly target by months per year
      scaledTarget = target * MONTHS_PER_YEAR;
      scaledCurrent = current * MONTHS_PER_YEAR;
    }

    // Determine decimals based on magnitude
    const formatValue = (val) => {
      if (val === 0) return '0';
      if (val < 10) return val.toFixed(1).replace(/\.0$/, '');
      return Math.round(val).toLocaleString();
    };

    return {
      current: formatValue(scaledCurrent),
      target: formatValue(scaledTarget),
      percentage: target > 0 ? Math.min(Math.round((current / target) * 100), 100) : 0
    };
  };

  // Format a dollar amount compactly ($1M, $750K, $50,000) for the GCI target display.
  const formatCurrency = (val) => {
    const n = Number(val) || 0;
    if (n >= 1000000) return `$${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 1)}M`;
    if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
    return `$${n.toLocaleString()}`;
  };

  const KPICard = ({ item }) => {
    // If organizing by 'Y', try to sum from history if possible, else fallback to projection
    // Actually, let's stick to consistent projection/scaling logic for simplicity & consistency unless we have real YTD
    const { current, target, percentage } = getScaledValues(
      goals[item.id] || 0,
      targets[item.id] || 0,
      organizeBy
    );

    return (
      <div className="rounded-xl border border-gray-100 p-5 shadow-sm h-full hover:shadow-md transition-shadow" style={{ backgroundColor: 'var(--c-panel-soft)' }}>
        <h3 className="text-[11px] font-bold text-gray-800 mb-6 uppercase tracking-wider flex items-center gap-2">
          {item.label}
          {autoTrackedIds.includes(item.id) && (
            <span
              className="text-[7px] font-black text-[#55C5D0] bg-[#E0F7FA] px-1.5 py-0.5 rounded-full tracking-wider"
              title="Automatically tracked from CRM activity"
            >
              AUTO
            </span>
          )}
        </h3>
        <div className="flex items-end justify-between">
          <div className="flex items-end space-x-2">
            <span className="text-3xl font-bold text-gray-900 leading-none tracking-tight">{current}</span>
            <div className="flex flex-col mb-0.5">
              <span className="text-[11px] text-gray-400 font-bold leading-none mb-0.5">/ {target}</span>
              <span className="text-[8px] text-gray-400 font-bold leading-none uppercase tracking-wider">target</span>
            </div>
          </div>
          <div className="flex flex-col items-end">
            <span className={`text-sm font-bold leading-none ${percentage >= 100 ? 'text-green-500' : 'text-[#FF4D4D]'}`}>
              {percentage}%
            </span>
            <span className={`text-[8px] opacity-60 mt-1 font-bold leading-none uppercase tracking-wider ${percentage >= 100 ? 'text-green-500' : 'text-[#FF4D4D]'}`}>
              achievement
            </span>
          </div>
        </div>
      </div>
    );
  };

  const HistoricalTrendCard = ({ item, allGoals }) => {
    const data = useMemo(() => {
      const months = [];
      const now = new Date();

      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const m = date.getMonth() + 1;
        const y = date.getFullYear();
        const goal = allGoals.find(g => g.month === m && g.year === y);

        // Map item.id to model fields
        const fieldMap = {
          calls_made: ['current_calls', 'calls_target'],
          text_sent: ['current_text', 'text_target'],
          email_sent: ['current_email', 'email_target'],
          door_knocks: ['current_door_knocks', 'door_knocks_target'],
          letter_drops: ['current_letter_drops', 'letter_drops_target'],
          social_media: ['current_social_media', 'social_media_target'],
          appraisals_completed: ['current_appraisal', 'appraisal_target'],
          listings_won: ['current_listing', 'listing_target'],
          sales_closed: ['current_sales', 'sales_target']
        };

        const [currField, targetField] = fieldMap[item.id] || [];
        const current = goal ? (goal[currField] || 0) : 0;
        const target = goal ? (goal[targetField] || 50) : 50;

        const percentage = target > 0 ? Math.min(Math.round((current / target) * 100), 100) : 0;

        months.push({
          name: date.toLocaleString('default', { month: 'short' }).toUpperCase(),
          value: percentage,
          actualValue: current,
          targetValue: target
        });
      }
      return months;
    }, [allGoals, item.id]);

    // Total for Last 6 Months
    const total = data.reduce((s, d) => s + d.actualValue, 0);

    return (
      <div className="rounded-xl p-6 mb-6 shadow-sm border border-gray-200" style={{ backgroundColor: 'var(--c-panel-soft)' }}>
        <div className="flex items-start justify-between mb-8">
          <div>
            <h4 className="text-xl font-bold text-gray-900 tracking-tight leading-none mb-2">{item.label}</h4>
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Monthly performance compared to goals</p>
          </div>
          <div className="px-5 py-3 rounded-xl flex flex-col items-center min-w-[140px] bg-[#E0F7FA] border border-[#B2EBF2]">
            <span className="text-[9px] text-[#006064] font-black uppercase tracking-widest mb-1">Total (6 Mo)</span>
            <div className="flex items-baseline space-x-1.5">
              <span className="text-2xl font-black text-gray-800 leading-none">{total}</span>
              <span className="text-[10px] font-bold text-gray-500 lowercase">{item.unit}</span>
            </div>
          </div>
        </div>

        <div className="w-full h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id={`colorValue-${item.id}`} x1="0" y1="0" x2="0" y2="100%">
                  <stop offset="5%" stopColor="#55C5D0" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#55C5D0" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#E5E7EB" strokeWidth={1} />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fontWeight: 700, fill: '#6B7280' }}
                dy={15}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fontWeight: 700, fill: '#6B7280' }}
                domain={[0, 100]}
                ticks={[0, 20, 40, 60, 80, 100]}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                  fontSize: '12px',
                  fontWeight: '600',
                  padding: '12px'
                }}
                formatter={(value, name, props) => {
                  return [`${value}% (${props.payload.actualValue}/${props.payload.targetValue})`, "Achievement"];
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#55C5D0"
                strokeWidth={3}
                fillOpacity={1}
                fill={`url(#colorValue-${item.id})`}
                dot={{ stroke: '#55C5D0', strokeWidth: 2, r: 4, fill: 'white' }}
                activeDot={{ r: 6, strokeWidth: 0, fill: '#55C5D0' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  if (loading && !currentGoal) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55C5D0]"></div>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4">
      {/* Header Section */}
      <div className="flex items-center justify-between pb-6 mb-8 border-b border-[#F1F1F1]">
        <h1 className="text-3xl font-bold text-gray-800 tracking-tight">KPI Tracking</h1>
        <button
          onClick={() => setEditingGoals(true)}
          className="bg-[#374151] hover:bg-gray-800 text-white px-6 py-2 rounded font-semibold text-xs transition-colors shadow-sm"
        >
          Edit Goals
        </button>
      </div>

      {/* Filters and Org Selection */}
      <div className="mb-8">
        <div className="inline-flex items-center p-2 rounded-xl border border-gray-100" style={{ backgroundColor: 'var(--c-panel-soft)' }}>
          <span className="text-sm text-gray-500 mr-4 ml-2 font-light">Organise by:</span>
          <div className="flex space-x-1">
            {['D', 'W', 'M', 'Y'].map(type => (
              <button
                key={type}
                onClick={() => setOrganizeBy(type)}
                className={`w-10 h-8 flex items-center justify-center text-[11px] font-bold rounded-lg transition-all ${organizeBy === type ? 'bg-white text-gray-900 shadow-sm border border-gray-200' : 'bg-transparent text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                  }`}
                title={type === 'D' ? 'Day' : type === 'W' ? 'Week' : type === 'M' ? 'Month' : 'Year'}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* GCI Target — drives the GCI Target Progress widget on the main dashboard */}
      <div className="mb-8 rounded-xl border border-[#B2EBF2] bg-[#E0F7FA] p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-white">
            <DollarSign className="w-5 h-5 text-[#55C5D0]" />
          </div>
          <div>
            <p className="text-[11px] font-black text-[#006064] uppercase tracking-widest leading-none mb-1.5">GCI Target</p>
            <p className="text-[11px] text-[#00838F] font-medium leading-none">Annual gross commission goal shown on your dashboard</p>
          </div>
        </div>
        <span className="text-3xl font-bold text-gray-900 tracking-tight">{formatCurrency(commissionTarget)}</span>
      </div>

      <p className="text-[12px] font-bold text-gray-800 mb-8 uppercase tracking-wider">Current Progress</p>

      {/* Current Progress Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
        {kpiItems.map(item => (
          <KPICard key={item.id} item={item} />
        ))}
      </div>

      {/* Historical Trends Section */}
      <div className="mb-8">
        <p className="text-[14px] font-bold text-gray-800 uppercase tracking-wider">
          Historical Trends <span className="text-[#55C5D0] ml-1 lowercase font-normal">(Last 6 Months)</span>
        </p>
      </div>

      {/* Trends Grid */}
      <div className="grid grid-cols-1 gap-8">
        {kpiItems.map(item => (
          <HistoricalTrendCard key={item.id} item={item} allGoals={allGoalsData} />
        ))}
      </div>

      {/* Edit Goals Modal */}
      {editingGoals && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl p-8 w-full max-w-4xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Update Monthly Goals</h2>
                <p className="text-gray-500 text-sm mt-1">Set your targets and update current progress for {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}</p>
              </div>
              <button
                onClick={() => setEditingGoals(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            {/* GCI Target — first goal you can edit; also updates the dashboard widget */}
            <div className="grid grid-cols-12 gap-6 items-center mb-6 p-3 rounded-lg bg-[#E0F7FA] border border-[#B2EBF2]">
              <div className="col-span-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-white">
                  <DollarSign className="w-4 h-4 text-[#55C5D0]" />
                </div>
                <label className="block text-sm font-bold text-gray-700 leading-none">GCI Target</label>
              </div>
              <div className="col-span-8">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">$</span>
                  <input
                    type="number"
                    value={commissionTarget}
                    onChange={(e) => setCommissionTarget(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg p-2.5 pl-7 focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent outline-none font-semibold text-gray-800 bg-white shadow-sm transition-all"
                    placeholder="1000000"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-6 mb-4 pb-4 border-b border-gray-100 px-2">
              <div className="col-span-4 text-xs font-bold text-gray-400 uppercase tracking-wider">Metric</div>
              <div className="col-span-4 text-xs font-bold text-gray-400 uppercase tracking-wider pl-2">Current (Month Actual)</div>
              <div className="col-span-4 text-xs font-bold text-gray-400 uppercase tracking-wider pl-2">Target (Month Goal)</div>
            </div>

            <div className="space-y-6 mb-8">
              {kpiItems.map(item => (
                <div key={item.id} className="grid grid-cols-12 gap-6 items-center hover:bg-gray-50 p-2 rounded-lg transition-colors">
                  <div className="col-span-4 flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-[#E0F7FA]">
                      <item.icon className="w-4 h-4 text-[#55C5D0]" />
                    </div>
                    <label className="block text-sm font-bold text-gray-700 leading-none">{item.label}</label>
                  </div>
                  <div className="col-span-4">
                    {autoTrackedIds.includes(item.id) ? (
                      <div className="w-full border border-gray-200 rounded-lg p-2.5 font-semibold text-gray-400 bg-gray-50 flex items-center justify-between cursor-not-allowed" title="Automatically tracked from CRM activity — cannot be edited manually">
                        <span>{progressValues[item.id] || 0}</span>
                        <span className="text-[8px] font-black text-[#55C5D0] bg-[#E0F7FA] px-1.5 py-0.5 rounded-full tracking-wider">AUTO</span>
                      </div>
                    ) : (
                      <input
                        type="number"
                        value={progressValues[item.id]}
                        onChange={(e) => setProgressValues({ ...progressValues, [item.id]: e.target.value })}
                        className="w-full border border-gray-200 rounded-lg p-2.5 focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent outline-none font-semibold text-gray-800 bg-white shadow-sm transition-all"
                        placeholder="0"
                      />
                    )}
                  </div>
                  <div className="col-span-4">
                    <input
                      type="number"
                      value={targets[item.id]}
                      onChange={(e) => setTargets({ ...targets, [item.id]: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg p-2.5 focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent outline-none font-semibold text-gray-800 bg-white shadow-sm transition-all"
                      placeholder="0"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="flex space-x-4 pt-4 border-t border-gray-100">
              <button
                onClick={() => setEditingGoals(false)}
                className="flex-1 border border-gray-300 py-3 rounded-xl font-bold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveGoals}
                className="flex-1 bg-[#2C3E50] text-white py-3 rounded-xl font-bold hover:bg-[#34495E] transition-colors shadow-lg shadow-gray-200"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPIPage;
